import React, { useEffect, useState } from 'react';
import { Loader2, Building2, LogOut, Info, ExternalLink } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from './ui/dialog';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { toast } from 'sonner';
import recruitmentApi from '../utils/agencyRecruitmentApi';

/**
 * Iter-228 — "My Agencies" widget for the talent dashboard.
 * Renders:
 *   • all agency affiliations (active, pending, removed)
 *   • requirements + contact info modal
 *   • Leave Agency action with reason capture
 */
const MyAgenciesWidget = () => {
  const [rosters, setRosters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [requirementsModal, setRequirementsModal] = useState(null);
  const [leaveDialog, setLeaveDialog] = useState({ open: false, roster: null });
  const [leaveReason, setLeaveReason] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await recruitmentApi.myAgencies();
      setRosters(r.data?.rosters || []);
    } catch (e) {
      // soft-fail
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const onLeave = async () => {
    if (!leaveDialog.roster) return;
    setBusy(true);
    try {
      await recruitmentApi.leaveAgency(leaveDialog.roster.agency_id, leaveReason || 'Talent-initiated leave');
      toast.success(`You've left ${leaveDialog.roster.company_name}`);
      setLeaveDialog({ open: false, roster: null });
      setLeaveReason('');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    } finally { setBusy(false); }
  };

  if (loading) {
    return (
      <Card data-testid="my-agencies-widget"><CardContent className="p-6 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-cyan-600" /></CardContent></Card>
    );
  }
  if (rosters.length === 0) {
    return (
      <Card data-testid="my-agencies-widget-empty">
        <CardContent className="p-6 text-center">
          <Building2 className="w-8 h-8 mx-auto text-slate-400 mb-2" />
          <p className="text-sm text-slate-600">You haven&apos;t joined any agency teams yet.</p>
          <p className="text-xs text-slate-500 mt-1">Use an agency&apos;s recruitment link to apply.</p>
        </CardContent>
      </Card>
    );
  }

  const active = rosters.filter((r) => r.status === 'active');
  const pending = rosters.filter((r) => r.status === 'pending_approval');
  const removed = rosters.filter((r) => r.status === 'removed' || r.status === 'rejected');

  return (
    <Card data-testid="my-agencies-widget">
      <CardContent className="p-5 space-y-4">
        <div className="flex items-center gap-2">
          <Building2 className="w-5 h-5 text-cyan-600" />
          <h3 className="font-semibold text-slate-900">My Agencies</h3>
          <Badge className="bg-slate-100 text-slate-700 text-[10px]">{active.length} active</Badge>
        </div>

        {active.map((r) => (
          <AgencyRow key={r.agency_id} r={r}
            onRequirements={() => setRequirementsModal(r)}
            onLeave={() => setLeaveDialog({ open: true, roster: r })}
          />
        ))}

        {pending.length > 0 && (
          <div className="pt-3 border-t border-slate-100">
            <p className="text-[10px] uppercase tracking-wider text-amber-700 font-bold mb-2">Pending applications</p>
            {pending.map((r) => (
              <div key={r.agency_id} className="flex items-center gap-2 text-sm" data-testid={`my-agencies-pending-${r.agency_id}`}>
                {r.logo_url ? <img src={r.logo_url} alt="" className="w-8 h-8 rounded object-cover" /> : <div className="w-8 h-8 rounded bg-slate-100" />}
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-slate-900 truncate">{r.company_name}</div>
                  <div className="text-xs text-slate-500">Awaiting agency approval</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {removed.length > 0 && (
          <details className="pt-3 border-t border-slate-100">
            <summary className="text-[10px] uppercase tracking-wider text-slate-500 font-bold cursor-pointer">Past affiliations ({removed.length})</summary>
            <div className="mt-2 space-y-2">
              {removed.map((r) => (
                <div key={r.agency_id} className="text-xs text-slate-600" data-testid={`my-agencies-past-${r.agency_id}`}>
                  <strong>{r.company_name}</strong> — {r.status}{r.removed_reason ? ` · ${r.removed_reason}` : ''}
                  {r.can_rejoin && r.slug && (
                    <a href={`/agency/${r.slug}`} className="ml-2 text-cyan-700 hover:underline inline-flex items-center gap-0.5">
                      rejoin <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
              ))}
            </div>
          </details>
        )}
      </CardContent>

      {/* Requirements modal */}
      <Dialog open={!!requirementsModal} onOpenChange={(o) => !o && setRequirementsModal(null)}>
        <DialogContent className="max-w-md" data-testid="agency-requirements-modal">
          <DialogHeader>
            <DialogTitle>{requirementsModal?.company_name} requirements</DialogTitle>
            <DialogDescription>What this agency expects from team members.</DialogDescription>
          </DialogHeader>
          {requirementsModal && (
            <div className="space-y-3">
              {(requirementsModal.agency_requirements || []).length === 0 ? (
                <p className="text-sm text-slate-500">No requirements listed.</p>
              ) : (
                <ul className="space-y-2">
                  {requirementsModal.agency_requirements.map((req, i) => (
                    <li key={i} className="bg-slate-50 rounded-lg p-3" data-testid={`req-item-${i}`}>
                      <div className="font-medium text-slate-900 text-sm">{req.label}</div>
                      {req.detail && <div className="text-xs text-slate-600 mt-0.5">{req.detail}</div>}
                    </li>
                  ))}
                </ul>
              )}
              {requirementsModal.contact_info && (requirementsModal.contact_info.email || requirementsModal.contact_info.phone) && (
                <div className="pt-3 border-t border-slate-100 text-xs text-slate-600">
                  <strong>Contact:</strong> {requirementsModal.contact_info.email || ''} {requirementsModal.contact_info.phone || ''}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Leave dialog */}
      <Dialog open={leaveDialog.open} onOpenChange={(o) => !o && setLeaveDialog({ open: false, roster: null })}>
        <DialogContent className="max-w-md" data-testid="leave-agency-dialog">
          <DialogHeader>
            <DialogTitle>Leave {leaveDialog.roster?.company_name}</DialogTitle>
            <DialogDescription>The agency will be notified. You can rejoin later if they allow it.</DialogDescription>
          </DialogHeader>
          <Label>Reason (optional)</Label>
          <Textarea value={leaveReason} onChange={(e) => setLeaveReason(e.target.value)} rows={3} data-testid="leave-reason-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setLeaveDialog({ open: false, roster: null })} disabled={busy}>Cancel</Button>
            <Button onClick={onLeave} disabled={busy} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="leave-confirm-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Leave agency'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};


const AgencyRow = ({ r, onRequirements, onLeave }) => (
  <div className="flex items-center gap-3 bg-white rounded-xl p-3 border border-slate-100" data-testid={`my-agencies-active-${r.agency_id}`}>
    {r.logo_url ? <img src={r.logo_url} alt="" className="w-12 h-12 rounded-lg object-cover" /> : <div className="w-12 h-12 rounded-lg bg-cyan-50 text-cyan-600 flex items-center justify-center"><Building2 className="w-5 h-5" /></div>}
    <div className="flex-1 min-w-0">
      <div className="font-semibold text-slate-900 truncate">{r.company_name}</div>
      {r.tagline && <div className="text-xs text-slate-500 truncate">{r.tagline}</div>}
      <div className="text-[10px] text-slate-400">joined {r.joined_at ? new Date(r.joined_at).toLocaleDateString() : '—'}</div>
    </div>
    <div className="flex items-center gap-1">
      {(r.agency_requirements?.length > 0 || r.contact_info) && (
        <Button size="sm" variant="ghost" onClick={onRequirements} data-testid={`view-requirements-${r.agency_id}`}>
          <Info className="w-3.5 h-3.5 mr-1" /> Info
        </Button>
      )}
      <Button size="sm" variant="ghost" onClick={onLeave} className="text-rose-600 hover:text-rose-700 hover:bg-rose-50" data-testid={`leave-${r.agency_id}`}>
        <LogOut className="w-3.5 h-3.5 mr-1" /> Leave
      </Button>
    </div>
  </div>
);

export default MyAgenciesWidget;
